// src/components/LoanCalculationForms/PaymentsSection.tsx
import React, {useMemo} from "react";
import {Payment} from "@/models/personModel";
import PaymentPopup, {PaymentData} from "../../Payment/PaymentsPopup";
import {formatDayjsDate} from "@/utils/dateUtils";

// shadcn
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {Badge} from "@/components/ui/badge";
import {Separator} from "@/components/ui/separator";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

// icons
import {Plus, Wallet} from "lucide-react";

interface PaymentsSectionProps {
    isDetailView: boolean;
    payments: Payment[];
    remainingDebt: number;
    earlyClosed: boolean;
    status: string;
    openPaymentPopup: boolean;
    onAddPayment: () => void;
    onClosePaymentPopup: () => void;
    onSavePayment: (payment: PaymentData) => void;
}

const formatSom = (v: number) =>
    new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "KGS",
        minimumFractionDigits: 0,
    }).format(v);

const PaymentsSection: React.FC<PaymentsSectionProps> = (
    {
        isDetailView,
        payments,
        remainingDebt,
        earlyClosed,
        status,
        openPaymentPopup,
        onAddPayment,
        onClosePaymentPopup,
        onSavePayment,
    }) => {

    const disabled = useMemo(
        () => earlyClosed || ["closedEarly", "returned", "closed"].includes(status),
        [earlyClosed, status]
    );

    const displayDebt = disabled ? 0 : Math.max(0, Number(remainingDebt || 0));

    if (!isDetailView) return null;

    return (
        <Card className="rounded-2xl">
            <CardHeader className="pb-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle className="text-base sm:text-lg">Платежи клиента</CardTitle>

                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="gap-2">
                            <Wallet className="h-3.5 w-3.5 text-muted-foreground"/>
                            <span className="text-muted-foreground">Остаток</span>
                            <span className="font-medium text-foreground">
                {formatSom(displayDebt)}
              </span>
                        </Badge>

                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={onAddPayment}
                            disabled={disabled}
                            className="h-9"
                        >
                            <Plus className="h-4 w-4 mr-1"/>
                            Добавить
                        </Button>
                    </div>
                </div>
            </CardHeader>

            <CardContent className="space-y-4">
                <PaymentPopup
                    open={openPaymentPopup}
                    onClose={onClosePaymentPopup}
                    onSave={onSavePayment}
                    maxAmount={displayDebt}
                />

                <Separator/>

                {payments.length > 0 ? (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[180px]">Дата</TableHead>
                                    <TableHead className="text-right">Сумма</TableHead>
                                </TableRow>
                            </TableHeader>

                            <TableBody>
                                {payments.map((payment) => (
                                    <TableRow key={payment.id}>
                                        <TableCell className="font-medium">
                                            {formatDayjsDate(payment.date)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {payment.type === "discount"
                                                ? `Скидка ${formatSom(Number(payment.amount || 0))}`
                                                : formatSom(Number(payment.amount || 0))}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                ) : (
                    <div className="text-sm text-muted-foreground">
                        Пока нет платежей
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default PaymentsSection;
